import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const tripId = searchParams.get('tripId');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/company`)
      .then((r) => setCompany(r.data))
      .catch(() => { /* company branding optional */ });
  }, []);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }

    const poll = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/payments/checkout/status/${sessionId}`);
        if (res.data.paymentStatus === 'paid') {
          setStatus('paid');
        } else if (res.data.status === 'expired') {
          setStatus('expired');
        } else if (attempts < 5) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('timeout');
        }
      } catch (err) {
        if (attempts < 5) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    poll();
  }, [sessionId, attempts]);

  const logoSrc = company?.logoUrl?.startsWith('http') ? company.logoUrl : `${BACKEND_URL}${company?.logoUrl || ''}`;

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex flex-col items-center justify-center p-4" data-testid="payment-success-page">
      {/* Company branding header */}
      {company && (
        <div className="mb-8 flex flex-col items-center gap-2">
          {company.logoUrl && (
            <img
              data-testid="payment-success-logo"
              src={logoSrc}
              alt={company.companyName}
              className="h-14 w-auto object-contain"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
          <div className="text-sm font-semibold text-slate-700" data-testid="payment-success-brand-name">
            {company.companyName}
          </div>
        </div>
      )}
      <Card className="max-w-md w-full p-8 text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="h-16 w-16 text-[#2C3E35] animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Verifying Payment...</h2>
            <p className="text-sm text-slate-500 mt-2">Please wait while we confirm your payment</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-10 w-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Payment Successful!</h2>
            <p className="text-sm text-slate-500 mt-2">Thank you for your payment. Your trip booking is confirmed.</p>
            {tripId && <p className="text-xs text-slate-400 mt-1 font-mono">Trip: {tripId}</p>}
          </>
        )}
        {(status === 'expired' || status === 'error' || status === 'timeout') && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="h-10 w-10 text-red-500" />
            </div>
            <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {status === 'expired' ? 'Payment Expired' : 'Payment Issue'}
            </h2>
            <p className="text-sm text-slate-500 mt-2">
              {status === 'expired' ? 'Your payment session has expired.' : 'We couldn\'t verify your payment. Please contact your travel consultant.'}
            </p>
          </>
        )}
        <Button onClick={() => navigate('/')} className="mt-6 bg-[#2C3E35] hover:bg-[#1E2B25] text-white rounded-sm" data-testid="payment-go-home">
          <ArrowLeft className="h-4 w-4 mr-2" />Go to Dashboard
        </Button>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
